import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Logo from '../../assets/images/AccessMatrix.png';

const LandingLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="landing-layout">
      {/* Skip to Content for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Header / Navigation */}
      <header className="landing-header">
        <div className="container-fluid px-4 py-3 d-flex align-items-center justify-content-between">
          <button
            onClick={() => navigate('/')}
            style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            aria-label="AccessMetrix Home"
          >
            <img src={Logo} alt="" style={{ height: '42px', objectFit: 'contain' }} />
          </button>

          {/* Desktop Nav Links */}
          <nav className="d-none d-md-flex align-items-center gap-4" aria-label="Main Navigation">
            <button
              onClick={() => navigate('/')}
              className={`nav-item-link ${isActive('/') ? 'active' : ''}`}
              aria-current={isActive('/') ? 'page' : undefined}
            >
              Home
            </button>
            <button
              onClick={() => navigate('/platform')}
              className={`nav-item-link ${isActive('/platform') ? 'active' : ''}`}
              aria-current={isActive('/platform') ? 'page' : undefined}
            >
              Platform
            </button>
            <button
              onClick={() => navigate('/solutions')}
              className={`nav-item-link ${isActive('/solutions') ? 'active' : ''}`}
              aria-current={isActive('/solutions') ? 'page' : undefined}
            >
              Solutions
            </button>
            <button
              onClick={() => navigate('/about-contact')}
              className={`nav-item-link ${isActive('/about-contact') ? 'active' : ''}`}
              aria-current={isActive('/about-contact') ? 'page' : undefined}
            >
              About & Contact
            </button>
          </nav>

          {/* Nav CTAs */}
          <div className="d-none d-md-flex align-items-center gap-3">
            <button className="btn btn-outline-brand rounded-pill px-4" onClick={() => navigate('/login')}>
              Sign In
            </button>
            <button className="btn btn-primary-brand rounded-pill px-4" onClick={() => navigate('/auth/register')}>
              Request a Demo
            </button>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            className="mobile-nav-toggle d-md-none border-0 bg-transparent"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            <div className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <nav className="mobile-nav d-md-none px-4 py-3" aria-label="Mobile Navigation">
            <div className="d-flex flex-column gap-3">
              <button
                onClick={() => {
                  navigate('/');
                  setMobileMenuOpen(false);
                }}
                className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`}
              >
                Home
              </button>
              <button
                onClick={() => {
                  navigate('/platform');
                  setMobileMenuOpen(false);
                }}
                className={`mobile-nav-link ${isActive('/platform') ? 'active' : ''}`}
              >
                Platform
              </button>
              <button
                onClick={() => {
                  navigate('/solutions');
                  setMobileMenuOpen(false);
                }}
                className={`mobile-nav-link ${isActive('/solutions') ? 'active' : ''}`}
              >
                Solutions
              </button>
              <button
                onClick={() => {
                  navigate('/about-contact');
                  setMobileMenuOpen(false);
                }}
                className={`mobile-nav-link ${isActive('/about-contact') ? 'active' : ''}`}
              >
                About & Contact
              </button>
              <hr className="my-2" />
              <button
                className="btn btn-outline-brand rounded-pill w-100 py-2"
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
              >
                Sign In
              </button>
              <button
                className="btn btn-primary-brand rounded-pill w-100 py-2"
                onClick={() => {
                  navigate('/auth/register');
                  setMobileMenuOpen(false);
                }}
              >
                Request a Demo
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* Main Content Area */}
      <main id="main-content" tabIndex="-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="landing-footer py-5 mt-5">
        <div className="container-fluid px-4">
          <div className="row g-4 justify-content-between">
            <div className="col-lg-4 col-md-6">
              <div className="mb-3">
                <img src={Logo} alt="AccessMetrix Logo" style={{ height: '36px', objectFit: 'contain' }} />
              </div>
              <p className="text-muted fs-6 mb-0" style={{ maxWidth: '320px' }}>
                AccessMetrix is the ultimate accessibility and code compliance auditing platform, helping organizations document audits and achieve inclusion.
              </p>
            </div>
            <div className="col-lg-3 col-md-6">
              <h6 className="fw-bold text-dark mb-3">Links</h6>
              <ul className="list-unstyled d-flex flex-column gap-2 fs-6">
                <li>
                  <button onClick={() => navigate('/')} className="btn btn-link p-0 text-muted text-decoration-none shadow-none text-start">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/platform')} className="btn btn-link p-0 text-muted text-decoration-none shadow-none text-start">
                    Platform
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/solutions')} className="btn btn-link p-0 text-muted text-decoration-none shadow-none text-start">
                    Solutions
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/about-contact')} className="btn btn-link p-0 text-muted text-decoration-none shadow-none text-start">
                    About & Contact
                  </button>
                </li>
              </ul>
            </div>
            <div className="col-lg-3 col-md-6">
              <h6 className="fw-bold text-dark mb-3">Legal & Support</h6>
              <ul className="list-unstyled d-flex flex-column gap-2 fs-6">
                <li>
                  <button onClick={() => navigate('/privacy-policy')} className="btn btn-link p-0 text-muted text-decoration-none shadow-none text-start">
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms-conditions')} className="btn btn-link p-0 text-muted text-decoration-none shadow-none text-start">
                    Terms & Conditions
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <span className="text-muted fs-6">
              &copy; {new Date().getFullYear()} Disability Inclusion Solutions, Inc. All rights reserved.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

LandingLayout.propTypes = {
  children: PropTypes.node.isRequired
};

export default LandingLayout;
